import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { AlertCircle, Trash2, RotateCcw, FileText } from 'lucide-react';
import { docsApi } from '../api/docs';
import { toast } from '../components/Toast';
import { formatRelativeTime } from '../lib/utils';

export function TrashPage() {
  const { wsId = '' } = useParams<{ wsId: string }>();
  const queryClient = useQueryClient();

  const { data: docs, isLoading, error } = useQuery({
    queryKey: ['trash', wsId],
    queryFn: () => docsApi.list(wsId, { trashed: true }),
    enabled: !!wsId,
  });

  const restoreMutation = useMutation({
    mutationFn: (docId: string) => docsApi.restore(wsId, docId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trash', wsId] });
      queryClient.invalidateQueries({ queryKey: ['docs', wsId] });
      toast.success('已恢复');
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : '恢复失败');
    },
  });

  const deletePermanentMutation = useMutation({
    mutationFn: (docId: string) => docsApi.deletePermanent(wsId, docId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trash', wsId] });
      toast.success('已永久删除');
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : '删除失败');
    },
  });

  return (
    <div className="mx-auto max-w-4xl px-8 py-8">
      <div className="flex items-center gap-3">
        <Trash2 className="h-6 w-6 text-text-tertiary" />
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#1a1a2e]">回收站</h1>
          <p className="text-sm text-text-secondary">30 天内可恢复已删除的文档和思维导图</p>
        </div>
      </div>

      {isLoading ? (
        <div className="mt-6 space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 animate-shimmer rounded-lg bg-bg-subtle" />
          ))}
        </div>
      ) : error ? (
        <div className="mt-6 flex flex-col items-center rounded-xl bg-bg-surface p-10 text-center ring-1 ring-black/5 shadow-sm">
          <AlertCircle className="h-8 w-8 text-danger" />
          <p className="mt-3 text-sm text-text-secondary">
            {error instanceof Error ? error.message : '加载失败'}
          </p>
        </div>
      ) : docs && docs.length > 0 ? (
        <div className="mt-6 card divide-y divide-border">
          {docs.map((doc) => (
            <div key={doc.id} className="flex items-center gap-4 p-4">
              {doc.emoji ? (
  <span className="text-lg">{doc.emoji}</span>
) : (
  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent-50">
    <FileText className="h-5 w-5 text-accent" />
  </div>
)}
              <div className="flex-1">
                <div className="font-medium">{doc.title || '未命名文档'}</div>
                <div className="text-xs text-text-tertiary">
                  {doc.type === 'doc' ? '文档' : '思维导图'} · 删除于{' '}
                  {formatRelativeTime(doc.updatedAt)}
                </div>
              </div>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => restoreMutation.mutate(doc.id)}
                disabled={restoreMutation.isPending}
                title="恢复"
              >
                <RotateCcw className="h-4 w-4" />
              </button>
              <button
                type="button"
                className="btn btn-ghost btn-sm text-danger"
                onClick={() => {
                  if (confirm('确定永久删除？此操作不可恢复。')) {
                    deletePermanentMutation.mutate(doc.id);
                  }
                }}
                disabled={deletePermanentMutation.isPending}
                title="永久删除"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-12 flex flex-col items-center text-center">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-bg-subtle text-text-tertiary">
            <Trash2 className="h-9 w-9" />
          </div>
          <p className="mt-4 text-base font-medium">回收站为空</p>
          <p className="mt-1 text-sm text-text-secondary">删除的文档和思维导图将出现在这里</p>
        </div>
      )}
    </div>
  );
}